// ./src/components/features/Dashboard/DashboardUpcomingServices.tsx
// Dashboard component showing upcoming, due soon, and overdue maintenance services.
// Refactored to use DashboardSection + DashboardItemTile reusable components.

import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { SERVICE_INTERVAL_TYPES } from "@/config";

import { formatDateTime } from "@/utils/dateHelpers";
import { getCarDisplayName } from "@/utils/carHelpers";

import { ServiceIntervalNext, UrgencyStatus } from "@/utils/api/ServiceIntervalNextService";

import { useUserProfileContext } from "@/contexts/UserProfileContext";
import { useAppEssentialsContext } from "@/contexts/AppEssentialsContext";

import { DashboardSection, CountBadge } from "./DashboardSection";
import {
  DashboardItemTile,
  TileBadge,
  TileAction,
  TileStatusType,
} from "./DashboardItemTile";

// ============================================
// Types
// ============================================

export interface DashboardUpcomingServicesProps {
  /** List of upcoming services (should be filtered to overdue/due_soon/upcoming) */
  services: ServiceIntervalNext[];

  /** Whether data is loading */
  loading?: boolean;

  /** Callback when "Complete" / "Add Record" button is clicked */
  onAddRecord?: (carId: string, kindId: number) => void;

  /** Callback when "View Report" button is clicked */
  onViewReport?: () => void;
}

// ============================================
// Constants
// ============================================

const URGENCY_ICONS: Record<UrgencyStatus, string> = {
  overdue: "AlertTriangle",
  due_soon: "Clock",
  upcoming: "Calendar",
  ok: "Check",
};

const URGENCY_COLORS: Record<UrgencyStatus, string> = {
  overdue: "red",
  due_soon: "orange",
  upcoming: "blue",
  ok: "gray",
};

// ============================================
// Mapping Helpers
// ============================================

/**
 * Map service urgency to tile badge status type.
 */
const getStatusBadgeType = (
  urgencyStatus: UrgencyStatus | undefined
): TileBadge | null => {
  switch (urgencyStatus) {
    case "overdue":
      return { type: "overdue" };
    case "due_soon":
      return { type: "due_soon" };
    case "upcoming":
      return { type: "upcoming" };
    default:
      return null;
  }
};

/**
 * Map urgency status to tile status type for bottom text color.
 */
const getStatusType = (
  urgencyStatus: UrgencyStatus | undefined
): TileStatusType => {
  switch (urgencyStatus) {
    case "overdue":
      return "error";
    case "due_soon":
      return "warning";
    case "upcoming":
      return "info";
    default:
      return "neutral";
  }
};

// ============================================
// Main Component
// ============================================

export const DashboardUpcomingServices: React.FC<DashboardUpcomingServicesProps> = ({
  services,
  loading = false,
  onAddRecord,
  onViewReport,
}) => {
  const { t } = useTranslation(["dashboard", "services", "common"]);
  const { userProfile } = useUserProfileContext();
  const { getExpenseKindNameById } = useAppEssentialsContext();

  const distanceUnit = userProfile?.distanceIn || "km";

  // Helper to get service name
  const getServiceName = useCallback(
    (service: ServiceIntervalNext): string => {
      if (service.expenseKind?.name) {
        return service.expenseKind.name;
      }

      if (service.kindId != null) {
        const name = getExpenseKindNameById(service.kindId);
        if (name) return name;
      }

      return t("common:upcomingServices.unknownService");
    },
    [getExpenseKindNameById, t]
  );

  // Count by urgency for header badges
  const countBadges = useMemo<CountBadge[]>(() => {
    const counts = { overdue: 0, due_soon: 0 };
    services.forEach((s) => {
      if (s.urgencyStatus === "overdue") counts.overdue++;
      else if (s.urgencyStatus === "due_soon") counts.due_soon++;
    });

    const badges: CountBadge[] = [];
    if (counts.overdue > 0) badges.push({ type: "error", count: counts.overdue });
    if (counts.due_soon > 0) badges.push({ type: "warning", count: counts.due_soon });
    return badges;
  }, [services]);

  // Build subtitle: "Vehicle Name · Due Date · Mileage"
  const getSubtitle = useCallback(
    (service: ServiceIntervalNext): string => {
      const parts: string[] = [];

      // Vehicle name
      const carName = service.car
        ? getCarDisplayName(service.car as any)
        : t("common:upcomingServices.unknownVehicle");
      parts.push(carName);

      // Determine what to show based on interval type
      const showDate =
        service.intervalType === SERVICE_INTERVAL_TYPES.DAYS ||
        service.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_OR_DAYS ||
        service.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_AND_DAYS;

      const showMileage =
        service.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE ||
        service.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_OR_DAYS ||
        service.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_AND_DAYS;

      if (showDate && service.nextWhenDo) {
        parts.push(formatDateTime(service.nextWhenDo, "medium"));
      }

      if (showMileage && service.nextOdometer) {
        parts.push(`${service.nextOdometer.toLocaleString()} ${distanceUnit}`);
      }

      return parts.join(" · ");
    },
    [distanceUnit, t]
  );

  // Build status text (remaining/overdue info)
  const getStatusText = useCallback(
    (service: ServiceIntervalNext): string | undefined => {
      const showDate =
        service.intervalType === SERVICE_INTERVAL_TYPES.DAYS ||
        service.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_OR_DAYS ||
        service.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_AND_DAYS;

      const showMileage =
        service.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE ||
        service.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_OR_DAYS ||
        service.intervalType === SERVICE_INTERVAL_TYPES.MILEAGE_AND_DAYS;

      const parts: string[] = [];

      if (showDate && service.remainingDays != null) {
        if (service.remainingDays < 0) {
          parts.push(
            t("common:upcomingServices.daysOverdue", {
              count: Math.abs(service.remainingDays),
            })
          );
        } else {
          parts.push(
            t("common:upcomingServices.daysRemaining", {
              count: service.remainingDays,
            })
          );
        }
      }

      if (showMileage && service.remainingMileage != null) {
        if (service.remainingMileage < 0) {
          parts.push(
            t("common:upcomingServices.mileageOverdue", {
              value: Math.abs(service.remainingMileage).toLocaleString(),
              unit: distanceUnit,
            })
          );
        } else {
          parts.push(
            t("common:upcomingServices.mileageRemaining", {
              value: service.remainingMileage.toLocaleString(),
              unit: distanceUnit,
            })
          );
        }
      }

      return parts.length > 0 ? parts.join(" / ") : undefined;
    },
    [distanceUnit, t]
  );

  // Get icon info from urgency
  const getIconInfo = useCallback(
    (service: ServiceIntervalNext): { iconName: string; iconColor: string } => {
      const status = service.urgencyStatus || "ok";
      return {
        iconName: URGENCY_ICONS[status],
        iconColor: URGENCY_COLORS[status],
      };
    },
    []
  );

  // Build badges for a service
  const getBadges = useCallback(
    (service: ServiceIntervalNext): TileBadge[] => {
      const badge = getStatusBadgeType(service.urgencyStatus);
      return badge ? [badge] : [];
    },
    []
  );

  // Build actions for a service
  const getActions = useCallback(
    (service: ServiceIntervalNext): TileAction[] => {
      if (!onAddRecord || !service.carId || service.kindId == null) return [];

      return [
        {
          type: "complete" as const,
          onClick: () => onAddRecord(service.carId!, service.kindId!),
          tooltip: t("common:upcomingServices.addRecord"),
        },
      ];
    },
    [onAddRecord, t]
  );

  return (
    <DashboardSection
      title={t("common:upcomingServices.title")}
      countBadges={countBadges}
      headerAction={
        onViewReport
          ? {
            label: t("common:upcomingServices.viewReport"),
            onClick: onViewReport,
          }
          : undefined
      }
      loading={loading}
      totalItems={services.length}
      emptyState={{
        title: t("common:upcomingServices.emptyTitle"),
        description: t("common:upcomingServices.emptyDescription"),
        iconName: "CalendarCheck",
      }}
    >
      {services.map((service) => {
        const { iconName, iconColor } = getIconInfo(service);
        return (
          <DashboardItemTile
            key={service.id || `${service.carId}-${service.kindId}`}
            title={getServiceName(service)}
            iconName={iconName}
            iconColor={iconColor}
            badges={getBadges(service)}
            subtitle={getSubtitle(service)}
            statusText={getStatusText(service)}
            statusType={getStatusType(service.urgencyStatus)}
            actions={getActions(service)}
          />
        );
      })}
    </DashboardSection>
  );
};